/**
 * Painel de um GRUPO do WhatsApp (coluna da direita): foto, assunto, descrição e participantes com
 * nome, foto e telefone formatado (quem não tem nome público aparece pelo número), administradores
 * primeiro. A assistente de IA nunca responde em grupo — o painel diz isso.
 */
import React, { type ReactElement } from "react";
import { Badge } from "../../ui/index.js";
import type { PlatformConversation } from "../../api/types.js";
import { ChatAvatar } from "./ChatAvatar.js";
import { stripJid } from "./helpers.js";

export function GroupPanel({ conversation }: { conversation: PlatformConversation }): ReactElement {
  const group = conversation.group;
  const participants = conversation.participants ?? [];
  const subject = group?.subject ?? conversation.displayName;
  return (
    <aside aria-label="Dados do grupo" className="hidden min-h-0 flex-col gap-5 overflow-y-auto border-l border-border p-5 2xl:flex" data-panel="group">
      <div className="flex flex-col items-center gap-2 text-center">
        <ChatAvatar name={subject} pictureUrl={group?.pictureUrl} size="lg" group />
        <h3 className="m-0 text-lg font-extrabold text-ink">{stripJid(subject)}</h3>
        <p className="m-0 text-sm text-muted">{`Grupo · ${group?.size ?? participants.length} participantes`}</p>
      </div>
      {group?.description ? (
        <section>
          <h4 className="m-0 mb-1 text-xs font-black uppercase tracking-wider text-muted">Descrição</h4>
          <p className="m-0 whitespace-pre-wrap text-sm text-ink">{group.description}</p>
        </section>
      ) : null}
      <section>
        <h4 className="m-0 mb-2 text-xs font-black uppercase tracking-wider text-muted">{`Participantes (${participants.length})`}</h4>
        {participants.length === 0 ? <p className="m-0 text-sm text-muted">Participantes ainda não sincronizados.</p> : (
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {participants.map((participant) => (
              <li key={participant.jid} className="flex items-center gap-2">
                <ChatAvatar name={participant.author.name} pictureUrl={participant.author.pictureUrl} size="sm" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-bold text-ink">{stripJid(participant.author.name)}</span>
                  {participant.author.hasName && participant.author.phoneDisplay ? <span className="block truncate text-xs text-muted">{participant.author.phoneDisplay}</span> : null}
                </span>
                {participant.isAdmin ? <Badge tone="success">Admin</Badge> : null}
              </li>
            ))}
          </ul>
        )}
      </section>
      <p className="m-0 rounded-control bg-surface-soft p-3 text-sm text-muted" data-ai-note="group">
        A assistente de IA fica <b className="text-ink">desligada em grupos</b>. Só conversas 1:1 podem receber resposta automática.
      </p>
    </aside>
  );
}
